// src/features/localization/LocalesSettings.tsx
// Admin: locales — code, name, native name, formats, RTL. ListLocales is the
// store's designed primary list (Locale is the store's item type).

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n, useLocalization } from '@react/hooks';
import { Alert, Badge, Button, Input } from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { useScope } from '@/hooks/useScope';
import type { Locale } from '@core/types/localization';
import { LocalizationModalShell } from './ModalShell';
import { errMessage, makeEntity } from './helpers';

export const LocalesSettings = observer(() => {
  const { t } = useI18n();
  const scope = useScope();
  const store = useLocalization();

  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Locale | null>(null);
  const [deleting, setDeleting] = useState<Locale | null>(null);
  const [busy, setBusy] = useState(false);

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [nativeName, setNativeName] = useState('');
  const [flagEmoji, setFlagEmoji] = useState('');
  const [isRtl, setIsRtl] = useState(false);

  const load = () => {
    setError(null);
    store.ListLocales().catch((e: unknown) => setError(errMessage(e, t('localization.locales.loadError'))));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openDialog = async (listed: Locale | null) => {
    let l = listed;
    if (listed?.entity?.id) {
      try {
        l = await store.GetLocale(listed.entity.id);
      } catch {
        l = listed;
      }
    }
    setEditing(l);
    setCode(l?.code ?? '');
    setName(l?.name ?? '');
    setNativeName(l?.native_name ?? '');
    setFlagEmoji(l?.flag_emoji ?? '');
    setIsRtl(l?.is_rtl ?? false);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!code.trim() || !name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      if (editing?.entity?.id) {
        await store.UpdateLocale(editing.entity.id, { locale: { ...editing, name: name.trim(), native_name: nativeName, flag_emoji: flagEmoji, is_rtl: isRtl } });
      } else {
        await store.CreateLocale({
          entity: makeEntity('', 'locale', scope),
          code: code.trim(),
          name: name.trim(),
          native_name: nativeName || name.trim(),
          flag_emoji: flagEmoji || undefined,
          is_rtl: isRtl,
        });
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      setError(errMessage(e, t('localization.locales.saveError')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('localization.locales.hint')}</p>
        <Button size="sm" onClick={() => openDialog(null)}>{t('localization.locales.newLocale')}</Button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}
      {store.isLoading && store.items.length === 0 && <div className="h-24 animate-pulse rounded-md bg-muted" aria-busy="true" />}
      {!store.isLoading && store.items.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">{t('localization.locales.empty')}</p>
        </div>
      )}

      <div className="space-y-2">
        {store.items.map((l) => (
          <div key={l.entity?.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground">{l.flag_emoji} {l.native_name || l.name}</span>
                <Badge variant="default">{l.code}</Badge>
                {l.status === 'LOCALE_STATUS_BETA' && <Badge variant="secondary">{t('localization.locales.beta')}</Badge>}
                {l.status === 'LOCALE_STATUS_INACTIVE' && <Badge variant="warning">{t('localization.locales.inactive')}</Badge>}
              </div>
            </div>
            <div className="flex shrink-0 gap-1.5">
              <Button size="sm" variant="secondary" onClick={() => openDialog(l)}>{t('common.edit')}</Button>
              <Button size="sm" variant="danger" onClick={() => setDeleting(l)}>{t('common.delete')}</Button>
            </div>
          </div>
        ))}
      </div>

      <LocalizationModalShell
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? t('localization.locales.editLocale') : t('localization.locales.newLocale')}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setDialogOpen(false)} disabled={busy}>{t('common.cancel')}</Button>
            <Button size="sm" onClick={save} isLoading={busy} disabled={!code.trim() || !name.trim()}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label={t('localization.locales.code')} value={code} onChange={(e) => setCode(e.target.value)} placeholder="en-US" disabled={!!editing} required />
          <Input label={t('common.name')} value={name} onChange={(e) => setName(e.target.value)} required />
          <Input label={t('localization.locales.nativeName')} value={nativeName} onChange={(e) => setNativeName(e.target.value)} />
          <Input label={t('localization.locales.flagEmoji')} value={flagEmoji} onChange={(e) => setFlagEmoji(e.target.value)} className="w-20" />
          <ToggleSwitch label={t('localization.locales.isRtl')} checked={isRtl} onChange={setIsRtl} />
        </div>
      </LocalizationModalShell>

      <ConfirmDialog
        open={!!deleting}
        title={t('localization.locales.deleteTitle')}
        description={t('localization.locales.deleteDescription')}
        destructive
        isConfirming={store.isDeleting}
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting?.entity?.id) return;
          try {
            await store.DeleteLocale(deleting.entity.id);
            setDeleting(null);
            load();
          } catch (e) {
            setError(errMessage(e, t('localization.locales.deleteError')));
            setDeleting(null);
          }
        }}
      />
    </div>
  );
});
